<template>
  <div class="login-container">
    <div class="login-left">
      <img src="/logo.png" alt="Team CRM" class="brand-logo" />
      <div class="brand-notice">
        <p>This portal is for authorized team members only.</p>
        <p>All access and requests are monitored.</p>
      </div>
    </div>
    <div class="login-right">
      <div class="login-form-wrapper">
        <h2 class="form-title">Sign in to your account</h2>
        <p class="form-subtitle">Enter your credentials to continue</p>
        <div class="form-grid" @keydown.enter="handleLogin">
          <div class="form-row">
            <label class="form-label">Email</label>
            <TextInput v-model="email" type="email" placeholder="you@example.com" ref="emailInput" />
          </div>
          <div class="form-row">
            <label class="form-label">Password</label>
            <TextInput v-model="password" type="password" placeholder="Enter your password" />
          </div>
          <div class="form-row">
            <label class="form-label">Stay signed in for</label>
            <SelectInput v-model="days" :options="durationOptions" />
          </div>
        </div>
        <button class="button primary form-button" :disabled="isLoading" @click="handleLogin">
          {{ isLoading ? 'Signing in...' : 'Sign In' }}
        </button>
        <a href="#" class="reset-password-link" @click.prevent="openResetModal">Forgot your password? Request a reset here</a>
        <div v-if="error" class="login-error">
          {{ error }}
        </div>
      </div>
    </div>

    <!-- Reset Password Modal -->
    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="showResetModal" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <div class="modal-card">
            <CloseIcon :size="24" class="close-modal-icon close-on-click" />
            <div class="modal-card-header">
              <h1 class="title">Reset Password</h1>
            </div>

            <!-- Step 1: Email + Secret Code -->
            <template v-if="resetStep === 1">
              <div class="modal-card-body">
                <div class="form-grid">
                  <div class="form-row">
                    <label class="form-label">Email</label>
                    <TextInput v-model="resetEmail" type="email" placeholder="you@example.com" />
                  </div>
                  <div class="form-row">
                    <label class="form-label">Secret Code</label>
                    <TextInput v-model="resetSecret" type="password" placeholder="Enter secret code" />
                  </div>
                </div>
              </div>
              <div class="modal-card-footer">
                <button class="button primary form-button" :disabled="isResetting" @click="handleVerifySecret">
                  {{ isResetting ? 'Verifying...' : 'Verify' }}
                </button>
              </div>
            </template>

            <!-- Step 2: New Password -->
            <template v-if="resetStep === 2">
              <div class="modal-card-body">
                <div class="form-grid">
                  <div class="form-row">
                    <label class="form-label">New Password</label>
                    <TextInput v-model="newPassword" type="password" placeholder="Enter new password" />
                  </div>
                </div>
              </div>
              <div class="modal-card-footer">
                <button class="button primary form-button" :disabled="isResetting" @click="handleResetPassword">
                  {{ isResetting ? 'Resetting...' : 'Reset Password' }}
                </button>
              </div>
            </template>

            <div v-if="resetMessage" :class="['modal-message', resetMessageType]">
              {{ resetMessage }}
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import TextInput from '@/components/inputs/TextInput.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import CloseIcon from 'vue-material-design-icons/Close.vue';
import * as api from '@/api/index.js';
import { alertService } from '@/libs/alert.js';
import { isExtensionMode } from '@/libs/extensionAuth';

// Available "stay signed in" durations (days). The extension is capped at 7 days.
const DURATION_OPTIONS = [
  { value: 1, label: '1 day' },
  { value: 3, label: '3 days' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' }
];
const EXTENSION_MAX_DAYS = 7;

export default {
  name: 'LoginView',
  components: {
    TextInput,
    SelectInput,
    CloseIcon
  },
  data() {
    return {
      email: '',
      password: '',
      days: isExtensionMode() ? EXTENSION_MAX_DAYS : 30,
      isLoading: false,
      error: null,
      showResetModal: false,
      resetStep: 1,
      resetEmail: '',
      resetSecret: '',
      newPassword: '',
      isResetting: false,
      resetMessage: null,
      resetMessageType: 'success'
    };
  },
  computed: {
    durationOptions() {
      // In the extension, don't allow staying signed in longer than 7 days.
      if (isExtensionMode()) {
        return DURATION_OPTIONS.filter(opt => opt.value <= EXTENSION_MAX_DAYS);
      }
      return DURATION_OPTIONS;
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.emailInput?.$el?.focus();
    });
    this.checkIfAlreadyLoggedIn();
  },
  methods: {
    async checkIfAlreadyLoggedIn() {
      try {
        const practices = await api.fetchPractices();

        if (practices && practices.length > 0) {
          const currentPracticeId = api.getPracticeId();

          if (!currentPracticeId) {
            api.setPracticeId(practices[0].practiceId);
          }

          this.$router.push('/patients');
        }
      } catch (error) {
        // User is not authenticated, stay on login page
      }
    },
    async handleLogin() {
      this.error = null;

      if (!this.email || !this.email.trim()) {
        this.error = 'Email is required';
        return;
      }

      if (!this.password || !this.password.trim()) {
        this.error = 'Password is required';
        return;
      }

      this.isLoading = true;
      try {
        await api.login(this.email, this.password, this.days);

        try {
          const practices = await api.fetchPractices();
          if (practices && practices.length > 0) {
            const currentPracticeId = api.getPracticeId();
            if (!currentPracticeId) {
              api.setPracticeId(practices[0].practiceId);
            }
          }
        } catch (practiceError) {
          console.error('Failed to fetch practices:', practiceError);
        }

        alertService.success('Login successful!');
        this.$router.push('/patients');
      } catch (error) {
        this.error = error.message || 'Login failed. Please check your credentials.';
        this.isLoading = false;
      }
    },
    openResetModal() {
      this.showResetModal = true;
      this.resetStep = 1;
      this.resetEmail = '';
      this.resetSecret = '';
      this.newPassword = '';
      this.resetMessage = null;
    },
    handleModalClick(event) {
      if (event.target.classList.contains('close-on-click')) {
        this.showResetModal = false;
      }
    },
    async handleVerifySecret() {
      if (!this.resetEmail || !this.resetEmail.trim()) {
        this.resetMessage = 'Email is required';
        this.resetMessageType = 'error';
        return;
      }
      if (!this.resetSecret || !this.resetSecret.trim()) {
        this.resetMessage = 'Secret code is required';
        this.resetMessageType = 'error';
        return;
      }

      this.isResetting = true;
      this.resetMessage = null;

      try {
        await api.verifyResetSecret(this.resetEmail, this.resetSecret);
        this.resetStep = 2;
        this.resetMessage = null;
      } catch (error) {
        this.resetMessage = error.message || 'Verification failed';
        this.resetMessageType = 'error';
      } finally {
        this.isResetting = false;
      }
    },
    async handleResetPassword() {
      if (!this.newPassword || !this.newPassword.trim()) {
        this.resetMessage = 'Password is required';
        this.resetMessageType = 'error';
        return;
      }

      this.isResetting = true;
      this.resetMessage = null;

      try {
        await api.resetPassword(this.resetEmail, this.resetSecret, this.newPassword);
        this.resetMessage = 'Password has been reset successfully';
        this.resetMessageType = 'success';
        setTimeout(() => {
          this.showResetModal = false;
        }, 1500);
      } catch (error) {
        this.resetMessage = error.message || 'Failed to reset password';
        this.resetMessageType = 'error';
      } finally {
        this.isResetting = false;
      }
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
}

.login-left {
  flex: 1;
  background: #1e3b5d;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 32px;
}

.brand-logo {
  width: 330px;
  max-width: 100%;
  height: auto;
  display: block;
}

.brand-notice {
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  line-height: 1.5;
}

.brand-notice p {
  margin: 0;
}

.login-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: #f8f9fb;
}

.login-form-wrapper {
  width: 100%;
  max-width: 380px;
}

.form-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 6px;
}

.form-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 32px;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 28px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.reset-password-link {
  display: block;
  margin-top: 14px;
  color: var(--primary-color);
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
}

.reset-password-link:hover {
  text-decoration: underline;
}

.login-error {
  margin-top: 16px;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--border-radius);
  color: #dc2626;
  font-size: 13px;
  text-align: center;
}

.modal-message {
  margin-top: 16px;
  font-size: 13px;
  text-align: center;
  padding: 10px 12px;
  border-radius: var(--border-radius);
}

.modal-message.success {
  color: #065f46;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}

.modal-message.error {
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
  }

  .login-left {
    flex: none;
    padding: 48px 24px;
  }

  .login-right {
    flex: 1;
    padding: 32px 24px;
  }
}
</style>
